import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip } from "@/components/ui/tooltip";
import { ToastContainer } from "@/components/ui/toast";
import {
  Search,
  Copy,
  Check,
  Book,
  Palette,
  Type,
  MousePointerClick,
  FormInput,
  Layers,
  Layout,
  Box,
  Sparkles,
  Eye,
  Smartphone,
  Zap,
  FileText,
  Table,
  AlertCircle,
  CheckCircle2,
  Info,
  Loader2,
  X,
  Upload,
  Grid,
  Circle,
} from "lucide-react";

const DesignSystemPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSection, setActiveSection] = useState("");
  const [copiedColor, setCopiedColor] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState("tab1");
  const [toasts, setToasts] = useState<any[]>([]);
  const [loadingState, setLoadingState] = useState(false);
  const sectionRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  // Scroll spy for navigation
  useEffect(() => {
    const handleScroll = () => {
      const sections = Object.keys(sectionRefs.current);
      for (const section of sections) {
        const element = sectionRefs.current[section];
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    setCopiedColor(type);
    setTimeout(() => setCopiedColor(null), 2000);
    showToast("Copied!", `${type} copied to clipboard`, "default");
  };

  const showToast = (
    title: string,
    description: string,
    variant: "default" | "destructive" | "success" = "default"
  ) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts([...toasts, { id, title, description, variant }]);
    setTimeout(() => {
      setToasts(toasts.filter((t) => t.id !== id));
    }, 3000);
  };

  const scrollToSection = (sectionId: string) => {
    const element = sectionRefs.current[sectionId];
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const sections = [
    { id: "colors", label: "Colors", icon: Palette },
    { id: "typography", label: "Typography", icon: Type },
    { id: "buttons", label: "Buttons", icon: MousePointerClick },
    { id: "forms", label: "Form Elements", icon: FormInput },
    { id: "badges", label: "Badges", icon: Layers },
    { id: "alerts", label: "Alerts", icon: AlertCircle },
    { id: "cards", label: "Cards", icon: Box },
    { id: "dialogs", label: "Dialogs", icon: Layout },
    { id: "selects", label: "Selects", icon: FormInput },
    { id: "tabs", label: "Tabs", icon: Layers },
    { id: "tooltips", label: "Tooltips", icon: Info },
    { id: "toasts", label: "Toasts", icon: Sparkles },
    { id: "icons", label: "Icons", icon: Grid },
    { id: "spacing", label: "Spacing", icon: Grid },
    { id: "shadows", label: "Shadows", icon: Layers },
    { id: "radius", label: "Border Radius", icon: Circle },
    { id: "states", label: "Interactive States", icon: Zap },
    { id: "tables", label: "Tables & Lists", icon: Table },
    { id: "empty", label: "Empty States", icon: FileText },
    { id: "responsive", label: "Responsive", icon: Smartphone },
    { id: "animations", label: "Animations", icon: Zap },
    { id: "accessibility", label: "Accessibility", icon: Eye },
    { id: "guidelines", label: "Guidelines", icon: Book },
  ];

  const filteredSections = sections.filter((section) =>
    section.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Navigation Sidebar */}
      <aside className="fixed top-0 left-0 z-40 w-64 h-screen pt-20 overflow-y-auto bg-white border-r border-gray-200">
        <div className="p-4">
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#495057] opacity-50" />
              <Input
                placeholder="Search components..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <nav className="space-y-1">
            {filteredSections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                    activeSection === section.id
                      ? "bg-primary text-primary-foreground"
                      : "text-[#495057] hover:bg-gray-100"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {section.label}
                </button>
              );
            })}
          </nav>
        </div>
      </aside>

      {/* Main Content */}
      <div className="px-4 py-12 ml-64 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-4xl font-heading font-bold text-primary">
                PhiloLMS Design System
              </h1>
              <Badge className="bg-[#16A34A] text-white">Stable</Badge>
            </div>
            <p className="text-lg text-[#495057]">
              A comprehensive guide to our design system components, following Atomic Design
              principles.
            </p>
          </div>

          {/* Color Palette */}
          <section
            ref={(el) => (sectionRefs.current.colors = el as HTMLElement | null)}
            id="colors"
            className="mb-16 scroll-mt-20"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-heading font-bold text-primary">
                  Color Palette
                </CardTitle>
                <CardDescription>
                  Our color system provides consistency and meaning across the application.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                <div>
                  <h3 className="text-lg font-semibold text-[#495057] mb-4">Primary Colors</h3>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <ColorSwatch
                      name="Primary (Warm Blue)"
                      hex="#2563EB"
                      description="The core brand color. A warmer, more approachable blue that represents trust, intelligence, and clarity. Softer on the eyes for extended admin sessions. Used for primary buttons, main navigation active states, and key data visualizations."
                      bgColor="#2563EB"
                      textColor="white"
                      onCopy={() => copyToClipboard("#2563EB", "Primary")}
                      copied={copiedColor === "Primary"}
                    />
                    <ColorSwatch
                      name="Secondary (Quiet Gray)"
                      hex="#495057"
                      description="A deep, neutral color for primary text, structural borders, and dark backgrounds (like a footer). Provides excellent readability and stability."
                      bgColor="#495057"
                      textColor="white"
                      onCopy={() => copyToClipboard("#495057", "Secondary")}
                      copied={copiedColor === "Secondary"}
                    />
                    <ColorSwatch
                      name="Accent (Vivid Teal)"
                      hex="#17A2B8"
                      description="A high-contrast color used for alerts, success messages, high-priority CTAs (e.g., 'Renew Book'), and dynamic charts. It adds a touch of modernity without being overwhelming."
                      bgColor="#17A2B8"
                      textColor="white"
                      onCopy={() => copyToClipboard("#17A2B8", "Accent")}
                      copied={copiedColor === "Accent"}
                    />
                    <ColorSwatch
                      name="Background (Clean White)"
                      hex="#F8F9FA"
                      description="Off-white/very light gray for content areas. Ensures maximum readability and visual breathing room."
                      bgColor="#F8F9FA"
                      textColor="#495057"
                      onCopy={() => copyToClipboard("#F8F9FA", "Background")}
                      copied={copiedColor === "Background"}
                    />
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-[#495057] mb-4">System Colors</h3>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ColorSwatch
                      name="System Success"
                      hex="#28A745"
                      description="Standard green for successful check-ins, saves, and confirmation."
                      bgColor="#28A745"
                      textColor="white"
                      onCopy={() => copyToClipboard("#16A34A", "Success")}
                      copied={copiedColor === "Success"}
                    />
                    <ColorSwatch
                      name="System Danger"
                      hex="#DC3545"
                      description="Standard red for overdue warnings, deletion prompts, and error messages."
                      bgColor="#DC3545"
                      textColor="white"
                      onCopy={() => copyToClipboard("#DC3545", "Danger")}
                      copied={copiedColor === "Danger"}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Typography */}
          <section
            ref={(el) => (sectionRefs.current.typography = el as HTMLElement | null)}
            id="typography"
            className="mb-16 scroll-mt-20"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-heading font-bold text-primary">
                  Typography
                </CardTitle>
                <CardDescription>
                  Typography establishes hierarchy and improves readability across the application.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                <div>
                  <h3 className="text-lg font-semibold text-[#495057] mb-4">Headings</h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm text-[#495057] opacity-70 mb-2 block">
                        H1 - Page Title
                      </Label>
                      <h1 className="text-4xl font-heading font-bold text-primary">
                        This is a Page Title
                      </h1>
                      <p className="text-sm text-[#495057] mt-2">
                        Font: Plus Jakarta Sans (Bold/Semi-Bold) | Color: Primary or Secondary
                        (#495057)
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm text-[#495057] opacity-70 mb-2 block">
                        H2 - Section Title
                      </Label>
                      <h2 className="text-3xl font-heading font-semibold text-[#495057]">
                        This is a Section Title
                      </h2>
                    </div>
                    <div>
                      <Label className="text-sm text-[#495057] opacity-70 mb-2 block">
                        H3 - Subsection Title
                      </Label>
                      <h3 className="text-2xl font-heading font-semibold text-[#495057]">
                        This is a Subsection Title
                      </h3>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-[#495057] mb-4">Body Text</h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm text-[#495057] opacity-70 mb-2 block">
                        Body Text (Regular)
                      </Label>
                      <p className="text-base text-[#495057]">
                        This is body text using Inter Regular at 14-16px. It provides excellent
                        readability for paragraphs, descriptions, and general content throughout the
                        application.
                      </p>
                      <p className="text-sm text-[#495057] mt-2">
                        Font: Inter (Regular) | Size: 14-16px | Color: Secondary (#495057)
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm text-[#495057] opacity-70 mb-2 block">
                        Body Text (Semi-Bold)
                      </Label>
                      <p className="text-base font-semibold text-[#495057]">
                        This is body text using Inter Semi-Bold for emphasis.
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm text-[#495057] opacity-70 mb-2 block">
                        Disabled/Helper Text
                      </Label>
                      <p className="text-sm text-[#495057] opacity-60">
                        This is helper or disabled text with reduced opacity for secondary
                        information.
                      </p>
                      <p className="text-sm text-[#495057] mt-2">
                        Font: Inter (Regular) | Color: Light Gray (Lighter shade of Secondary)
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Buttons */}
          <section
            ref={(el) => (sectionRefs.current.buttons = el as HTMLElement | null)}
            id="buttons"
            className="mb-16 scroll-mt-20"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-heading font-bold text-primary">
                  Buttons (Atoms)
                </CardTitle>
                <CardDescription>
                  Interactive elements that trigger actions. Buttons follow consistent styling and
                  behavior patterns.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                <div>
                  <h3 className="text-lg font-semibold text-[#495057] mb-4">Primary Buttons</h3>
                  <div className="flex flex-wrap items-center gap-4">
                    <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                      Primary Button
                    </Button>
                    <Button
                      className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
                      size="sm"
                    >
                      Small Primary
                    </Button>
                    <Button
                      className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
                      size="lg"
                    >
                      Large Primary
                    </Button>
                    <Button
                      className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
                      disabled
                    >
                      Disabled
                    </Button>
                  </div>
                  <p className="text-sm text-[#495057] mt-4">
                    Font: Inter (Semi-Bold) | Background: Primary | Text: White
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-[#495057] mb-4">Accent Buttons</h3>
                  <div className="flex flex-wrap items-center gap-4">
                    <Button className="bg-[#17A2B8] text-white hover:bg-[#17A2B8]/90 font-semibold">
                      Accent Button
                    </Button>
                    <Button
                      className="bg-[#17A2B8] text-white hover:bg-[#17A2B8]/90 font-semibold"
                      size="sm"
                    >
                      Small Accent
                    </Button>
                    <Button
                      className="bg-[#17A2B8] text-white hover:bg-[#17A2B8]/90 font-semibold"
                      size="lg"
                    >
                      Large Accent
                    </Button>
                  </div>
                  <p className="text-sm text-[#495057] mt-4">
                    Font: Inter (Semi-Bold) | Background: Accent (#17A2B8) | Text: White
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-[#495057] mb-4">Secondary Buttons</h3>
                  <div className="flex flex-wrap items-center gap-4">
                    <Button variant="outline">Outline Button</Button>
                    <Button variant="secondary">Secondary Button</Button>
                    <Button variant="ghost">Ghost Button</Button>
                    <Button variant="link">Link Button</Button>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-[#495057] mb-4">Destructive Buttons</h3>
                  <div className="flex flex-wrap items-center gap-4">
                    <Button variant="destructive">Delete</Button>
                    <Button variant="destructive" size="sm">
                      Small Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Form Elements */}
          <section
            ref={(el) => (sectionRefs.current.forms = el as HTMLElement | null)}
            id="forms"
            className="mb-16 scroll-mt-20"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-heading font-bold text-primary">
                  Form Elements (Atoms)
                </CardTitle>
                <CardDescription>
                  Basic form input components that can be combined into more complex forms.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                <div>
                  <h3 className="text-lg font-semibold text-[#495057] mb-4">Input Fields</h3>
                  <div className="max-w-md space-y-4">
                    <div>
                      <Label htmlFor="example-input">Label</Label>
                      <Input id="example-input" placeholder="Enter text..." />
                    </div>
                    <div>
                      <Label htmlFor="example-input-disabled">Disabled Input</Label>
                      <Input id="example-input-disabled" placeholder="Disabled..." disabled />
                    </div>
                    <div>
                      <Label htmlFor="example-textarea">Textarea</Label>
                      <Textarea id="example-textarea" placeholder="Enter multiple lines..." />
                    </div>
                    <div>
                      <Label htmlFor="example-input-error">Input with Error</Label>
                      <Input
                        id="example-input-error"
                        placeholder="Invalid input..."
                        className="border-[#DC3545] focus:ring-[#DC3545]"
                      />
                      <p className="text-sm text-[#DC3545] mt-1">This field is required</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-[#495057] mb-4">Checkboxes</h3>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox id="checkbox-1" />
                      <Label htmlFor="checkbox-1" className="cursor-pointer">
                        Checkbox option 1
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="checkbox-2" checked />
                      <Label htmlFor="checkbox-2" className="cursor-pointer">
                        Checkbox option 2 (checked)
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="checkbox-3" disabled />
                      <Label htmlFor="checkbox-3" className="opacity-50 cursor-pointer">
                        Checkbox option 3 (disabled)
                      </Label>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-[#495057] mb-4">Radio Buttons</h3>
                  <div className="space-y-3">
                    {["Option 1", "Option 2", "Option 3"].map((option, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id={`radio-${idx}`}
                          name="radio-group"
                          defaultChecked={idx === 0}
                          className="w-4 h-4 text-primary focus:ring-primary"
                        />
                        <Label htmlFor={`radio-${idx}`} className="cursor-pointer">
                          {option}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-[#495057] mb-4">File Upload</h3>
                  <div className="max-w-md p-8 text-center border-2 border-gray-300 border-dashed rounded-lg">
                    <Upload className="h-12 w-12 text-[#495057] opacity-50 mx-auto mb-4" />
                    <Label htmlFor="file-upload" className="cursor-pointer">
                      <span className="text-primary font-semibold">Click to upload</span> or drag
                      and drop
                    </Label>
                    <Input id="file-upload" type="file" className="hidden" />
                    <p className="text-sm text-[#495057] opacity-60 mt-2">
                      PNG, JPG, PDF up to 10MB
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Badges */}
          <section
            ref={(el) => (sectionRefs.current.badges = el as HTMLElement | null)}
            id="badges"
            className="mb-16 scroll-mt-20"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-heading font-bold text-primary">
                  Badges (Atoms)
                </CardTitle>
                <CardDescription>Small status indicators and labels.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  <Badge>Default</Badge>
                  <Badge variant="secondary">Secondary</Badge>
                  <Badge variant="destructive">Destructive</Badge>
                  <Badge className="bg-[#17A2B8] text-white">Accent</Badge>
                  <Badge className="bg-[#16A34A] text-white">Success</Badge>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Alerts */}
          <section
            ref={(el) => (sectionRefs.current.alerts = el as HTMLElement | null)}
            id="alerts"
            className="mb-16 scroll-mt-20"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-heading font-bold text-primary">
                  Alerts (Molecules)
                </CardTitle>
                <CardDescription>
                  Alert messages that combine text, icons, and sometimes actions to communicate
                  important information.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert>
                  <AlertDescription>This is a default alert message.</AlertDescription>
                </Alert>
                <Alert className="bg-[#17A2B8]/10 border-[#17A2B8]">
                  <AlertDescription className="text-[#17A2B8]">
                    This is an accent alert using the Vivid Teal color.
                  </AlertDescription>
                </Alert>
                <Alert className="bg-[#16A34A]/10 border-[#16A34A]">
                  <AlertDescription className="text-[#16A34A]">
                    Success! This is a success message using System Success color.
                  </AlertDescription>
                </Alert>
                <Alert className="bg-[#DC3545]/10 border-[#DC3545]">
                  <AlertDescription className="text-[#DC3545]">
                    Error! This is an error message using System Danger color.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </section>

          {/* Cards */}
          <section
            ref={(el) => (sectionRefs.current.cards = el as HTMLElement | null)}
            id="cards"
            className="mb-16 scroll-mt-20"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-heading font-bold text-primary">
                  Cards (Molecules)
                </CardTitle>
                <CardDescription>
                  Card components combine multiple atoms to create structured content containers.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Card>
                    <CardHeader>
                      <CardTitle>Card Title</CardTitle>
                      <CardDescription>Card description goes here.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-[#495057]">
                        This is the card content area. Cards are used to group related information
                        and actions.
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle>Another Card</CardTitle>
                      <CardDescription>With different content.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-[#495057]">
                        Cards provide visual separation and organization.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Dialogs */}
          <section
            ref={(el) => (sectionRefs.current.dialogs = el as HTMLElement | null)}
            id="dialogs"
            className="mb-16 scroll-mt-20"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-heading font-bold text-primary">
                  Dialogs (Molecules)
                </CardTitle>
                <CardDescription>
                  Modal dialogs for important interactions that require user attention.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={() => setDialogOpen(true)}
                  className="bg-primary text-primary-foreground"
                >
                  Open Dialog
                </Button>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogContent onClose={() => setDialogOpen(false)}>
                    <DialogHeader>
                      <DialogTitle>Dialog Title</DialogTitle>
                      <DialogDescription>
                        This is a dialog example. Dialogs are used for confirmations, forms, and
                        important information.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                      <p className="text-sm text-[#495057]">
                        Dialog content goes here. You can include forms, text, or any other content.
                      </p>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button
                        className="bg-primary text-primary-foreground"
                        onClick={() => setDialogOpen(false)}
                      >
                        Confirm
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </section>

          {/* Selects */}
          <section
            ref={(el) => (sectionRefs.current.selects = el as HTMLElement | null)}
            id="selects"
            className="mb-16 scroll-mt-20"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-heading font-bold text-primary">
                  Select Dropdowns (Molecules)
                </CardTitle>
                <CardDescription>
                  Dropdown select components for choosing from a list of options.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="max-w-md">
                  <Label>Select an option</Label>
                  <Select defaultValue="option1">
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="option1">Option 1</SelectItem>
                      <SelectItem value="option2">Option 2</SelectItem>
                      <SelectItem value="option3">Option 3</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Tabs */}
          <section
            ref={(el) => (sectionRefs.current.tabs = el as HTMLElement | null)}
            id="tabs"
            className="mb-16 scroll-mt-20"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-heading font-bold text-primary">
                  Tabs (Molecules)
                </CardTitle>
                <CardDescription>
                  Tab components for organizing content into multiple panels.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs value={tabValue} onValueChange={setTabValue}>
                  <TabsList>
                    <TabsTrigger value="tab1">Tab 1</TabsTrigger>
                    <TabsTrigger value="tab2">Tab 2</TabsTrigger>
                    <TabsTrigger value="tab3">Tab 3</TabsTrigger>
                  </TabsList>
                  <TabsContent value="tab1">
                    <p className="text-sm text-[#495057]">Content for Tab 1 goes here.</p>
                  </TabsContent>
                  <TabsContent value="tab2">
                    <p className="text-sm text-[#495057]">Content for Tab 2 goes here.</p>
                  </TabsContent>
                  <TabsContent value="tab3">
                    <p className="text-sm text-[#495057]">Content for Tab 3 goes here.</p>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </section>

          {/* Tooltips */}
          <section
            ref={(el) => (sectionRefs.current.tooltips = el as HTMLElement | null)}
            id="tooltips"
            className="mb-16 scroll-mt-20"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-heading font-bold text-primary">
                  Tooltips (Molecules)
                </CardTitle>
                <CardDescription>Tooltips provide additional context on hover.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4">
                  <Tooltip content="This is a tooltip on the right">
                    <Button variant="outline">Hover me (Right)</Button>
                  </Tooltip>
                  <Tooltip content="This is a tooltip on the top" side="top">
                    <Button variant="outline">Hover me (Top)</Button>
                  </Tooltip>
                  <Tooltip content="This is a tooltip on the bottom" side="bottom">
                    <Button variant="outline">Hover me (Bottom)</Button>
                  </Tooltip>
                  <Tooltip content="This is a tooltip on the left" side="left">
                    <Button variant="outline">Hover me (Left)</Button>
                  </Tooltip>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Toasts */}
          <section
            ref={(el) => (sectionRefs.current.toasts = el as HTMLElement | null)}
            id="toasts"
            className="mb-16 scroll-mt-20"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-heading font-bold text-primary">
                  Toasts (Molecules)
                </CardTitle>
                <CardDescription>
                  Toast notifications for temporary feedback messages.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4">
                  <Button
                    onClick={() =>
                      showToast("Success", "Operation completed successfully!", "success")
                    }
                    className="bg-[#16A34A] text-white"
                  >
                    Show Success Toast
                  </Button>
                  <Button
                    onClick={() => showToast("Error", "Something went wrong!", "destructive")}
                    variant="destructive"
                  >
                    Show Error Toast
                  </Button>
                  <Button
                    onClick={() => showToast("Info", "Here's some information", "default")}
                    variant="outline"
                  >
                    Show Info Toast
                  </Button>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Icons */}
          <section
            ref={(el) => (sectionRefs.current.icons = el as HTMLElement | null)}
            id="icons"
            className="mb-16 scroll-mt-20"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-heading font-bold text-primary">
                  Iconography
                </CardTitle>
                <CardDescription>
                  We use Lucide React icons. Icons should be consistent in size and style.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-6 md:grid-cols-8">
                  {[
                    { icon: Book, name: "Book" },
                    { icon: Search, name: "Search" },
                    { icon: CheckCircle2, name: "Check" },
                    { icon: AlertCircle, name: "Alert" },
                    { icon: Info, name: "Info" },
                    { icon: Loader2, name: "Loader" },
                    { icon: X, name: "Close" },
                    { icon: Copy, name: "Copy" },
                  ].map(({ icon: Icon, name }) => (
                    <div key={name} className="flex flex-col items-center gap-2">
                      <div className="p-3 bg-gray-100 rounded-lg">
                        <Icon className="h-6 w-6 text-[#495057]" />
                      </div>
                      <span className="text-xs text-[#495057]">{name}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6">
                  <h4 className="font-semibold text-[#495057] mb-2">Icon Sizes</h4>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Search className="h-3 w-3 text-[#495057]" />
                      <span className="text-sm text-[#495057]">12px (xs)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Search className="h-4 w-4 text-[#495057]" />
                      <span className="text-sm text-[#495057]">16px (sm)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Search className="h-5 w-5 text-[#495057]" />
                      <span className="text-sm text-[#495057]">20px (md)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Search className="h-6 w-6 text-[#495057]" />
                      <span className="text-sm text-[#495057]">24px (lg)</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Spacing */}
          <section
            ref={(el) => (sectionRefs.current.spacing = el as HTMLElement | null)}
            id="spacing"
            className="mb-16 scroll-mt-20"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-heading font-bold text-primary">
                  Spacing & Layout
                </CardTitle>
                <CardDescription>
                  Consistent spacing creates visual rhythm and improves readability.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-[#495057] mb-4">Spacing Scale</h3>
                    <div className="space-y-2">
                      {[4, 8, 12, 16, 24, 32, 48, 64].map((size) => (
                        <div key={size} className="flex items-center gap-4">
                          <div className="w-20 text-sm text-[#495057]">{size}px</div>
                          <div className={`h-4 bg-primary`} style={{ width: `${size}px` }}></div>
                          <div className="text-xs text-[#495057] opacity-60">
                            p-{size / 4} / m-{size / 4}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#495057] mb-4">Grid System</h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {[1, 2, 3].map((col) => (
                        <div
                          key={col}
                          className="p-4 bg-gray-100 rounded text-center text-sm text-[#495057]"
                        >
                          {col} Column{col > 1 ? "s" : ""}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Shadows */}
          <section
            ref={(el) => (sectionRefs.current.shadows = el as HTMLElement | null)}
            id="shadows"
            className="mb-16 scroll-mt-20"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-heading font-bold text-primary">
                  Shadows & Elevation
                </CardTitle>
                <CardDescription>Shadow system for creating depth and hierarchy.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                  {[
                    { name: "sm", class: "shadow-sm", desc: "Small shadow for subtle elevation" },
                    { name: "md", class: "shadow-md", desc: "Medium shadow for cards" },
                    { name: "lg", class: "shadow-lg", desc: "Large shadow for modals" },
                  ].map((shadow) => (
                    <div key={shadow.name} className={`p-6 bg-white rounded-lg ${shadow.class}`}>
                      <h4 className="font-semibold text-[#495057] mb-2">{shadow.name}</h4>
                      <p className="text-sm text-[#495057] opacity-70">{shadow.desc}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Border Radius */}
          <section
            ref={(el) => (sectionRefs.current.radius = el as HTMLElement | null)}
            id="radius"
            className="mb-16 scroll-mt-20"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-heading font-bold text-primary">
                  Border Radius
                </CardTitle>
                <CardDescription>
                  Consistent border radius values for rounded elements.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-center gap-6">
                  {[
                    { name: "None", class: "rounded-none", value: "0px" },
                    { name: "sm", class: "rounded-sm", value: "2px" },
                    { name: "md", class: "rounded-md", value: "4px" },
                    { name: "lg", class: "rounded-lg", value: "8px" },
                    { name: "xl", class: "rounded-xl", value: "12px" },
                    { name: "Full", class: "rounded-full", value: "9999px" },
                  ].map((radius) => (
                    <div key={radius.name} className="flex flex-col items-center gap-2">
                      <div className={`w-20 h-20 bg-primary ${radius.class}`}></div>
                      <span className="text-sm font-semibold text-[#495057]">{radius.name}</span>
                      <span className="text-xs text-[#495057] opacity-60">{radius.value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Interactive States */}
          <section
            ref={(el) => (sectionRefs.current.states = el as HTMLElement | null)}
            id="states"
            className="mb-16 scroll-mt-20"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-heading font-bold text-primary">
                  Interactive States
                </CardTitle>
                <CardDescription>Different states for interactive elements.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                <div>
                  <h3 className="text-lg font-semibold text-[#495057] mb-4">Button States</h3>
                  <div className="flex flex-wrap gap-4">
                    <Button className="bg-primary text-primary-foreground">Default</Button>
                    <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
                      Hover
                    </Button>
                    <Button className="bg-primary text-primary-foreground focus:ring-2 focus:ring-primary focus:ring-offset-2">
                      Focus
                    </Button>
                    <Button className="bg-primary text-primary-foreground active:bg-primary/80">
                      Active
                    </Button>
                    <Button
                      className="bg-primary text-primary-foreground opacity-50 cursor-not-allowed"
                      disabled
                    >
                      Disabled
                    </Button>
                    <Button
                      className="bg-primary text-primary-foreground"
                      disabled={loadingState}
                      onClick={() => {
                        setLoadingState(true);
                        setTimeout(() => setLoadingState(false), 2000);
                      }}
                    >
                      {loadingState ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        "Loading State"
                      )}
                    </Button>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#495057] mb-4">Input States</h3>
                  <div className="max-w-md space-y-4">
                    <div>
                      <Label>Default</Label>
                      <Input placeholder="Default state" />
                    </div>
                    <div>
                      <Label>Focus</Label>
                      <Input placeholder="Focus state" className="ring-2 ring-primary" />
                    </div>
                    <div>
                      <Label>Error</Label>
                      <Input
                        placeholder="Error state"
                        className="border-[#DC3545] focus:ring-[#DC3545]"
                      />
                      <p className="text-sm text-[#DC3545] mt-1">This field has an error</p>
                    </div>
                    <div>
                      <Label>Disabled</Label>
                      <Input placeholder="Disabled state" disabled />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Tables & Lists */}
          <section
            ref={(el) => (sectionRefs.current.tables = el as HTMLElement | null)}
            id="tables"
            className="mb-16 scroll-mt-20"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-heading font-bold text-primary">
                  Tables & Lists (Organisms)
                </CardTitle>
                <CardDescription>
                  Data display components for organizing information.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                <div>
                  <h3 className="text-lg font-semibold text-[#495057] mb-4">Table</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left p-3 text-sm font-semibold text-[#495057]">
                            Name
                          </th>
                          <th className="text-left p-3 text-sm font-semibold text-[#495057]">
                            Status
                          </th>
                          <th className="text-left p-3 text-sm font-semibold text-[#495057]">
                            Role
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { name: "John Doe", status: "Active", role: "Admin" },
                          { name: "Jane Smith", status: "Active", role: "User" },
                          { name: "Bob Johnson", status: "Inactive", role: "User" },
                        ].map((row, idx) => (
                          <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="p-3 text-sm text-[#495057]">{row.name}</td>
                            <td className="p-3">
                              <Badge
                                className={row.status === "Active" ? "bg-[#16A34A]" : "bg-gray-400"}
                              >
                                {row.status}
                              </Badge>
                            </td>
                            <td className="p-3 text-sm text-[#495057]">{row.role}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#495057] mb-4">List</h3>
                  <ul className="space-y-2">
                    {["Item 1", "Item 2", "Item 3"].map((item, idx) => (
                      <li key={idx} className="flex items-center gap-3 p-3 rounded bg-gray-50">
                        <CheckCircle2 className="h-5 w-5 text-[#16A34A]" />
                        <span className="text-sm text-[#495057]">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Empty States */}
          <section
            ref={(el) => (sectionRefs.current.empty = el as HTMLElement | null)}
            id="empty"
            className="mb-16 scroll-mt-20"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-heading font-bold text-primary">
                  Empty States
                </CardTitle>
                <CardDescription>
                  Placeholder states when there's no content to display.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="p-12 text-center border-2 border-gray-300 border-dashed rounded-lg">
                  <FileText className="h-16 w-16 text-[#495057] opacity-30 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-[#495057] mb-2">No items found</h3>
                  <p className="text-sm text-[#495057] opacity-70 mb-4">
                    Get started by creating your first item.
                  </p>
                  <Button className="bg-primary text-primary-foreground">Create Item</Button>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Responsive */}
          <section
            ref={(el) => (sectionRefs.current.responsive = el as HTMLElement | null)}
            id="responsive"
            className="mb-16 scroll-mt-20"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-heading font-bold text-primary">
                  Responsive Breakpoints
                </CardTitle>
                <CardDescription>Breakpoints for responsive design.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { name: "Mobile", size: "< 640px", class: "sm:hidden" },
                    { name: "Tablet", size: "640px - 1024px", class: "hidden sm:block lg:hidden" },
                    { name: "Desktop", size: "> 1024px", class: "hidden lg:block" },
                  ].map((bp) => (
                    <div key={bp.name} className={`p-4 bg-gray-100 rounded ${bp.class}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-[#495057]">{bp.name}</h4>
                          <p className="text-sm text-[#495057] opacity-70">{bp.size}</p>
                        </div>
                        <Smartphone className="h-6 w-6 text-[#495057] opacity-50" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Animations */}
          <section
            ref={(el) => (sectionRefs.current.animations = el as HTMLElement | null)}
            id="animations"
            className="mb-16 scroll-mt-20"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-heading font-bold text-primary">
                  Animations & Transitions
                </CardTitle>
                <CardDescription>Animation guidelines for smooth interactions.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-[#495057] mb-3">Transition Timing</h4>
                    <div className="space-y-2">
                      {[
                        { name: "Fast", value: "150ms", class: "transition-all duration-150" },
                        { name: "Normal", value: "200ms", class: "transition-all duration-200" },
                        { name: "Slow", value: "300ms", class: "transition-all duration-300" },
                      ].map((timing) => (
                        <div key={timing.name} className="flex items-center gap-4">
                          <div
                            className={`w-32 h-12 bg-primary rounded ${timing.class} hover:bg-[#17A2B8]`}
                          ></div>
                          <div>
                            <span className="font-semibold text-[#495057]">{timing.name}</span>
                            <span className="text-sm text-[#495057] opacity-70 ml-2">
                              {timing.value}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#495057] mb-3">Loading Animation</h4>
                    <div className="flex items-center gap-4">
                      <Loader2 className="h-8 w-8 text-primary animate-spin" />
                      <span className="text-sm text-[#495057]">Spinning loader animation</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Accessibility */}
          <section
            ref={(el) => (sectionRefs.current.accessibility = el as HTMLElement | null)}
            id="accessibility"
            className="mb-16 scroll-mt-20"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-heading font-bold text-primary">
                  Accessibility Guidelines
                </CardTitle>
                <CardDescription>Best practices for accessible design.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-[#495057] mb-3">Color Contrast</h4>
                    <ul className="list-disc list-inside space-y-2 text-sm text-[#495057]">
                      <li>Text on Primary: White (#FFFFFF) - WCAG AAA compliant</li>
                      <li>Text on Secondary: White (#FFFFFF) - WCAG AAA compliant</li>
                      <li>Body text: #495057 on #F8F9FA - WCAG AA compliant</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#495057] mb-3">Keyboard Navigation</h4>
                    <ul className="list-disc list-inside space-y-2 text-sm text-[#495057]">
                      <li>All interactive elements should be keyboard accessible</li>
                      <li>Use Tab key to navigate, Enter/Space to activate</li>
                      <li>Focus indicators should be clearly visible</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#495057] mb-3">ARIA Attributes</h4>
                    <ul className="list-disc list-inside space-y-2 text-sm text-[#495057]">
                      <li>Use aria-label for icon-only buttons</li>
                      <li>Use aria-describedby for form field descriptions</li>
                      <li>Use role attributes for custom components</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Usage Guidelines */}
          <section
            ref={(el) => (sectionRefs.current.guidelines = el as HTMLElement | null)}
            id="guidelines"
            className="mb-16 scroll-mt-20"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-heading font-bold text-primary">
                  Usage Guidelines
                </CardTitle>
                <CardDescription>
                  Best practices for using the design system components.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-[#495057] mb-3">Color Usage</h3>
                    <ul className="list-disc list-inside space-y-2 text-[#495057]">
                      <li>Use Primary Blue for main actions and navigation</li>
                      <li>Use Accent Teal for high-priority actions and alerts</li>
                      <li>Use System Success for positive feedback</li>
                      <li>Use System Danger for errors and warnings</li>
                      <li>Maintain sufficient contrast ratios for accessibility</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#495057] mb-3">Typography</h3>
                    <ul className="list-disc list-inside space-y-2 text-[#495057]">
                      <li>Use Plus Jakarta Sans for headings to create visual hierarchy</li>
                      <li>Use Inter for body text for optimal readability</li>
                      <li>Maintain consistent font sizes across similar elements</li>
                      <li>Ensure text contrast meets WCAG AA standards</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#495057] mb-3">Components</h3>
                    <ul className="list-disc list-inside space-y-2 text-[#495057]">
                      <li>Follow Atomic Design principles: Atoms → Molecules → Organisms</li>
                      <li>Reuse components rather than creating new ones</li>
                      <li>Maintain consistency in spacing and sizing</li>
                      <li>Test components for accessibility and responsiveness</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>
        </div>
      </div>

      {/* Toast Container */}
      <ToastContainer
        toasts={toasts}
        onClose={(id) => setToasts(toasts.filter((t) => t.id !== id))}
      />
    </div>
  );
};

// Color Swatch Component with Copy Functionality
const ColorSwatch = ({
  name,
  hex,
  description,
  bgColor,
  onCopy,
  copied,
}: {
  name: string;
  hex: string;
  description: string;
  bgColor: string;
  textColor: string;
  onCopy: () => void;
  copied: boolean;
}) => {
  return (
    <div className="overflow-hidden transition-shadow border rounded-lg hover:shadow-md">
      <div className="relative h-24" style={{ backgroundColor: bgColor }}>
        <button
          onClick={onCopy}
          className="absolute top-2 right-2 p-1.5 bg-white/20 hover:bg-white/30 rounded backdrop-blur-sm transition-colors"
          title="Click to copy"
        >
          {copied ? (
            <Check className="w-4 h-4 text-white" />
          ) : (
            <Copy className="w-4 h-4 text-white" />
          )}
        </button>
      </div>
      <div className="p-4 bg-white">
        <h4 className="font-semibold text-[#495057] mb-1">{name}</h4>
        <button
          onClick={onCopy}
          className="text-sm font-mono text-primary hover:underline mb-2 block"
        >
          {hex}
        </button>
        <p className="text-sm text-[#495057] opacity-70">{description}</p>
      </div>
    </div>
  );
};

export default DesignSystemPage;
